import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  author?: string;
  publishDate?: string;
  modifyDate?: string;
  articleBody?: string;
  keywords?: string;
  imageAlt?: string;
  structuredData?: Record<string, any>;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Axioma - Prompts & Herramientas IA',
  description = 'Descubre y comparte prompts de IA, herramientas y comunidad tech',
  canonical,
  ogImage = 'https://axioma.javzdev.com/og-image.png',
  ogType = 'website',
  ogUrl,
  author,
  publishDate,
  modifyDate,
  keywords,
  imageAlt,
  structuredData
}) => {
  const baseUrl = 'https://axioma.javzdev.com';
  const canonicalUrl = canonical || ogUrl || baseUrl + window.location.pathname;

  // Default Organization Schema
  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Axioma',
    url: baseUrl,
    logo: 'https://axioma.javzdev.com/logo512.png',
    description: description,
    sameAs: [
      'https://twitter.com/javzdev',
      'https://github.com/javzdev'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      url: baseUrl
    }
  };

  // Merge with custom structured data if provided
  const finalSchema = structuredData || defaultSchema;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="format-detection" content="telephone=no" />

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Alternate Links for Multilingual */}
      <link rel="alternate" hrefLang="es" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl.replace(/es\//g, 'en/')} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:site_name" content="Axioma" />
      <meta property="og:locale" content="es_ES" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article Meta Tags (if applicable) */}
      {publishDate && <meta property="article:published_time" content={publishDate} />}
      {modifyDate && <meta property="article:modified_time" content={modifyDate} />}
      {author && <meta property="article:author" content={author} />}

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(finalSchema)}
      </script>

      {/* Language */}
      <html lang="es" />
    </Helmet>
  );
};

export default SEO;
